import type { Cluster, Marker, MarkerClusterer } from "@googlemaps/markerclusterer";
import clusterComponent from "../map-cluster/index.marko";
import stackedClusterComponent from "../map-marker/index.marko";
import type { PointOfInterest } from "../../@types/mapviewxs";
import type { PreviewCardPosition } from "../../utils/preview-card-position";
import { isSameBound } from "./helpers";
import type { ItemCardControls, MapAccessibilityTexts, MarkerSelectionState, MarkerWithCircle } from "./types";
import { ACTIVE_MARKER_Z_INDEX, MARKER_Z_INDEX } from "./const";

interface ClusterRendererProps {
  markerLibrary: google.maps.MarkerLibrary;
  count: number;
  position: google.maps.LatLng;
  bounds: google.maps.LatLngBounds;
  clusterState: MarkerSelectionState;
  itemCardControls: ItemCardControls;
  selectedCluster?: Cluster | null;
  highlightedCluster?: Cluster | null;
  isStackedItems?: boolean;
  clusterPointsOfInterest?: PointOfInterest[];
  selectedMarker: MarkerWithCircle | null;
  mapAccessibilityTexts?: MapAccessibilityTexts;
}

interface CreateClusterComponentProps {
  count: number;
  itemCardControls?: ItemCardControls;
  isStackedItems?: boolean;
  clusterPointsOfInterest?: PointOfInterest[];
  defaultPreviewPosition?: PreviewCardPosition;
  selectedMarker: MarkerWithCircle | null;
  onPreviewClose?: () => void;
  mapAccessibilityTexts?: MapAccessibilityTexts;
}

interface CreateClusterActiveComponentProps extends CreateClusterComponentProps {
  selectionState: MarkerSelectionState;
}

export const clusterRenderer = ({
  markerLibrary,
  count,
  position,
  bounds,
  clusterState,
  itemCardControls,
  selectedCluster,
  highlightedCluster,
  isStackedItems,
  clusterPointsOfInterest,
  selectedMarker,
  mapAccessibilityTexts,
}: ClusterRendererProps): google.maps.marker.AdvancedMarkerElement => {
  const defaultZIndex = isStackedItems ? MARKER_Z_INDEX : count;
  const createMarkerElement = (state: MarkerSelectionState) => {
    const clusterContent = createActiveClusterComponent({
      count,
      selectionState: state,
      isStackedItems,
      clusterPointsOfInterest,
      itemCardControls,
      selectedMarker,
      mapAccessibilityTexts,
    });
    return new markerLibrary.AdvancedMarkerElement({
      position,
      content: clusterContent,
      zIndex: state === "selected" ? ACTIVE_MARKER_Z_INDEX : defaultZIndex,
    });
  };

  if (selectedCluster && isSameBound(selectedCluster.bounds!, bounds)) {
    selectedCluster.marker = createMarkerElement(clusterState);
    return selectedCluster.marker;
  }

  if (highlightedCluster && isSameBound(highlightedCluster.bounds!, bounds)) {
    highlightedCluster.marker = createMarkerElement(clusterState);
    return highlightedCluster.marker;
  }

  return new markerLibrary.AdvancedMarkerElement({
    position,
    content: createClusterComponent({
      count,
      itemCardControls,
      isStackedItems,
      clusterPointsOfInterest,
      selectedMarker: null,
      mapAccessibilityTexts,
    }),
    zIndex: defaultZIndex,
  });
};

export const createClusterItems = (markers: Marker[] = [], pointsOfInterest: PointOfInterest[] = []) => {
  const clusterItemsIndex = markers?.map((mark) => parseInt((mark as any).id));

  return pointsOfInterest?.filter((_, index) => clusterItemsIndex?.includes(index));
};

export const createClusterComponent = ({
  count,
  itemCardControls,
  isStackedItems,
  clusterPointsOfInterest,
  selectedMarker,
  onPreviewClose,
  defaultPreviewPosition,
  mapAccessibilityTexts,
}: CreateClusterComponentProps) => {
  if (isStackedItems) {
    return stackedClusterComponent
      .renderSync({
        animationDelay: 0,
        selectionState: "unselected",
        itemCards: clusterPointsOfInterest,
        itemCardControls,
        onPreviewClose,
        defaultPreviewPosition,
        selectedMarker,
        mapAccessibilityTexts,
      })
      .getNode();
  }
  return clusterComponent
    .renderSync({
      amount: count,
      animationDelay: 0,
      selectionState: "unselected",
      mapAccessibilityTexts,
    })
    .getNode();
};

export const createActiveClusterComponent = ({
  count,
  selectionState,
  isStackedItems,
  clusterPointsOfInterest,
  itemCardControls,
  selectedMarker,
  onPreviewClose,
  defaultPreviewPosition,
  mapAccessibilityTexts,
}: CreateClusterActiveComponentProps) => {
  if (isStackedItems) {
    return stackedClusterComponent
      .renderSync({
        animationDelay: 0,
        selectionState,
        itemCards: clusterPointsOfInterest,
        itemCardControls,
        onPreviewClose,
        defaultPreviewPosition,
        selectedMarker,
        mapAccessibilityTexts,
      })
      .getNode();
  }
  return clusterComponent
    .renderSync({
      amount: count,
      animationDelay: 0,
      selectionState,
      mapAccessibilityTexts,
    })
    .getNode();
};

export const findClusterGroup = (clusterGroup: MarkerClusterer, targetMarker: Marker): Cluster | undefined => {
  const clusters = (clusterGroup as any).clusters;
  for (const cluster of clusters) {
    const found = cluster.markers?.some((marker: Marker) => marker === targetMarker);
    if (found) {
      return cluster;
    }
  }
};
